import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Loader2, Upload, X, Trash2 } from "lucide-react";
import { BUSINESS_TYPE_OPTIONS } from "@/business/api";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const HOURS = (() => {
  const arr = [];
  for (let h = 0; h < 24; h++) {
    arr.push(`${String(h).padStart(2, "0")}:00`);
    arr.push(`${String(h).padStart(2, "0")}:30`);
  }
  return arr;
})();

function parseHourString(v) {
  // Backend may store hours as either:
  //   { mon: { open: "09:00", close: "22:00", closed: false }, ... }
  //   { mon: "09:00-22:00", ... }
  // Normalise to the object form for UI editing.
  if (!v) return {};
  if (typeof v === "string") {
    if (/^closed$/i.test(v.trim())) return { closed: true };
    const m = v.match(/^\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})\s*$/);
    if (m) return { open: m[1], close: m[2], closed: false };
    return {};
  }
  return { open: v.open || "", close: v.close || "", closed: !!v.closed };
}

function normalizeHoursObject(hours) {
  const out = {};
  if (!hours || typeof hours !== "object") return out;
  for (const day of DAYS) {
    out[day.key] = parseHourString(hours[day.key]);
  }
  return out;
}

function PhotoUploader({ label, value, onChange, testId, aspectClass = "aspect-video" }) {
  const inputRef = useRef(null);
  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image too large — keep it under 4 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div data-testid={testId}>
      <Label className="mb-2 block">{label}</Label>
      {value ? (
        <div
          className={`relative rounded-xl overflow-hidden border border-[#E5E7EB] bg-white ${aspectClass}`}
        >
          <img src={value} alt={label} className="absolute inset-0 h-full w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-black/60 text-white text-xs px-2 py-1 hover:bg-black/80"
            data-testid={`${testId}-remove-button`}
          >
            <Trash2 className="h-3 w-3" /> Remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`w-full ${aspectClass} rounded-xl border-2 border-dashed border-[#E5E7EB] hover:border-[#FF6A00]/60 bg-white text-[#6B7280] flex flex-col items-center justify-center gap-1 transition-colors duration-150`}
          data-testid={`${testId}-upload-button`}
        >
          <Upload className="h-5 w-5" />
          <span className="text-sm">Click to upload</span>
          <span className="text-[11px]">PNG, JPG, up to 4 MB</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid={`${testId}-file-input`}
      />
    </div>
  );
}

function GalleryUploader({ value = [], onChange }) {
  const inputRef = useRef(null);
  const onFiles = async (files) => {
    if (!files || !files.length) return;
    const list = Array.from(files).slice(0, 8 - value.length);
    const results = [];
    for (const f of list) {
      if (f.size > 4 * 1024 * 1024) {
        toast.error(`${f.name}: keep images under 4 MB.`);
        continue;
      }
      const dataUrl = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(f);
      });
      results.push(dataUrl);
    }
    if (results.length) onChange([...value, ...results]);
  };
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div data-testid="venue-gallery-uploader">
      <Label className="mb-2 block">Gallery (up to 8)</Label>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {value.map((src, i) => (
          <div
            key={i}
            className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E7EB] bg-white group"
            data-testid={`venue-gallery-tile-${i}`}
          >
            <img src={src} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-1 right-1 inline-flex items-center rounded-md bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 hover:bg-black/80 transition-opacity duration-150"
              data-testid={`venue-gallery-remove-button-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {value.length < 8 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="aspect-square rounded-lg border-2 border-dashed border-[#E5E7EB] hover:border-[#FF6A00]/60 bg-white text-[#6B7280] flex flex-col items-center justify-center gap-1"
            data-testid="venue-gallery-add-button"
          >
            <Upload className="h-4 w-4" />
            <span className="text-xs">Add photo</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => onFiles(e.target.files)}
        data-testid="venue-gallery-file-input"
      />
    </div>
  );
}

/**
 * <VenueForm>
 *
 * Reusable create / edit form for a single business venue (location).
 *
 * Props:
 *   initial: venue object (or null for create). Fields are snake_case.
 *   defaultCountry: string to prefill the country on a brand-new venue.
 *   submitting: boolean — disables submit while parent is awaiting the API.
 *   submitLabel: string — defaults to "Save venue".
 *   onSubmit(payload): async parent handler. payload is snake_case ready for
 *                      POST /api/business/locations or
 *                      PUT  /api/business/locations/{id}.
 *   onCancel: optional cancel button handler.
 *   data-testid scope: "venue-form".
 */
export default function VenueForm({
  initial,
  defaultCountry = "",
  submitting = false,
  submitLabel = "Save venue",
  onSubmit,
  onCancel,
}) {
  const [form, setForm] = useState(() => buildInitial(initial, defaultCountry));
  const [err, setErr] = useState("");

  useEffect(() => {
    setForm(buildInitial(initial, defaultCountry));
    setErr("");
  }, [initial, defaultCountry]);

  const upd = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const setDayHours = (day, patch) =>
    setForm((s) => ({
      ...s,
      hours: {
        ...s.hours,
        [day]: { ...(s.hours?.[day] || {}), ...patch },
      },
    }));

  const buildPayload = () => {
    const trim = (s) => (typeof s === "string" ? s.trim() : s);
    const payload = {
      business_name: trim(form.business_name),
      business_type: form.business_type || "other",
      accepts_buskers: !!form.accepts_buskers,
      hours: form.hours || {},
      gallery: Array.isArray(form.gallery) ? form.gallery : [],
      profile_photo: form.profile_photo || null,
      cover_photo: form.cover_photo || null,
    };
    const optionalStrings = [
      "address",
      "city",
      "country",
      "postal_code",
      "phone",
      "business_email",
      "website",
      "description",
    ];
    for (const k of optionalStrings) {
      const v = trim(form[k]);
      if (v) payload[k] = v;
    }
    if (form.latitude !== "" && form.latitude !== null && form.latitude !== undefined) {
      const lat = Number(form.latitude);
      if (Number.isFinite(lat)) payload.latitude = lat;
    }
    if (form.longitude !== "" && form.longitude !== null && form.longitude !== undefined) {
      const lng = Number(form.longitude);
      if (Number.isFinite(lng)) payload.longitude = lng;
    }
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.business_name || !form.business_name.trim()) {
      setErr("Business name is required.");
      return;
    }
    try {
      await onSubmit?.(buildPayload());
    } catch (e2) {
      setErr(e2?.message || "Could not save venue. Please try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="venue-form" noValidate>
      {/* Basics */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-[#111827]">Basics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="business_name">
              Business name <span className="text-[#DC2626]">*</span>
            </Label>
            <Input
              id="business_name"
              value={form.business_name}
              onChange={upd("business_name")}
              data-testid="venue-business-name-input"
              required
            />
          </div>
          <div>
            <Label htmlFor="business_type">Business type</Label>
            <Select
              value={form.business_type || "cafe"}
              onValueChange={(v) => setForm((s) => ({ ...s, business_type: v }))}
            >
              <SelectTrigger id="business_type" data-testid="venue-business-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={form.website || ""}
              onChange={upd("website")}
              data-testid="venue-website-input"
              placeholder="https://"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="description">About</Label>
            <Textarea
              id="description"
              value={form.description || ""}
              onChange={upd("description")}
              rows={3}
              data-testid="venue-description-input"
            />
          </div>
          <label
            className="md:col-span-2 flex items-center justify-between gap-3 rounded-lg border border-[#E5E7EB] p-3"
            data-testid="venue-accepts-buskers-row"
          >
            <div>
              <div className="text-sm font-medium">Open to buskers</div>
              <div className="text-xs text-[#6B7280]">
                Show this venue to performers near you.
              </div>
            </div>
            <Switch
              checked={!!form.accepts_buskers}
              onCheckedChange={(v) => setForm((s) => ({ ...s, accepts_buskers: v }))}
              data-testid="venue-accepts-buskers-switch"
            />
          </label>
        </div>
      </section>

      {/* Location */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-[#111827]">Location</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="address">Street address</Label>
            <Input
              id="address"
              value={form.address || ""}
              onChange={upd("address")}
              data-testid="venue-address-input"
            />
          </div>
          <div>
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              value={form.city || ""}
              onChange={upd("city")}
              data-testid="venue-city-input"
            />
          </div>
          <div>
            <Label htmlFor="country">Country</Label>
            <Input
              id="country"
              value={form.country || ""}
              onChange={upd("country")}
              data-testid="venue-country-input"
            />
          </div>
          <div>
            <Label htmlFor="postal_code">Postal code</Label>
            <Input
              id="postal_code"
              value={form.postal_code || ""}
              onChange={upd("postal_code")}
              data-testid="venue-postal-code-input"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={form.phone || ""}
              onChange={upd("phone")}
              data-testid="venue-phone-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="business_email">Business email</Label>
            <Input
              id="business_email"
              type="email"
              value={form.business_email || ""}
              onChange={upd("business_email")}
              placeholder="hello@yourvenue.com"
              data-testid="venue-business-email-input"
            />
          </div>
          <div>
            <Label htmlFor="latitude">Latitude</Label>
            <Input
              id="latitude"
              inputMode="decimal"
              value={form.latitude}
              onChange={upd("latitude")}
              placeholder="e.g. -33.86"
              data-testid="venue-latitude-input"
            />
          </div>
          <div>
            <Label htmlFor="longitude">Longitude</Label>
            <Input
              id="longitude"
              inputMode="decimal"
              value={form.longitude}
              onChange={upd("longitude")}
              placeholder="e.g. 151.20"
              data-testid="venue-longitude-input"
            />
          </div>
        </div>
      </section>

      {/* Hours */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-[#111827]">Hours of operation</h3>
        <Accordion type="multiple" className="w-full" data-testid="hours-editor">
          {DAYS.map((d) => {
            const day = form.hours?.[d.key] || {};
            return (
              <AccordionItem key={d.key} value={d.key} className="border-[#E5E7EB]">
                <AccordionTrigger
                  className="py-3 hover:no-underline"
                  data-testid={`hours-day-trigger-${d.key}`}
                >
                  <div className="flex items-center justify-between w-full pr-3">
                    <span className="text-sm font-medium">{d.label}</span>
                    <span className="text-xs text-[#6B7280]">
                      {day.closed
                        ? "Closed"
                        : day.open && day.close
                        ? `${day.open} – ${day.close}`
                        : "Not set"}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-2">
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-[#E5E7EB] p-3">
                      <span className="text-sm">Closed all day</span>
                      <Switch
                        checked={!!day.closed}
                        onCheckedChange={(v) => setDayHours(d.key, { closed: v })}
                        data-testid={`hours-day-toggle-${d.key}`}
                      />
                    </label>
                    <div>
                      <Label>Opens</Label>
                      <Select
                        value={day.open || ""}
                        onValueChange={(v) => setDayHours(d.key, { open: v })}
                        disabled={!!day.closed}
                      >
                        <SelectTrigger data-testid={`hours-open-select-${d.key}`}>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {HOURS.map((h) => (
                            <SelectItem key={h} value={h}>
                              {h}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Closes</Label>
                      <Select
                        value={day.close || ""}
                        onValueChange={(v) => setDayHours(d.key, { close: v })}
                        disabled={!!day.closed}
                      >
                        <SelectTrigger data-testid={`hours-close-select-${d.key}`}>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {HOURS.map((h) => (
                            <SelectItem key={h} value={h}>
                              {h}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </section>

      {/* Photos */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-[#111827]">Photos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <PhotoUploader
            label="Profile photo (logo)"
            value={form.profile_photo}
            onChange={(v) => setForm((s) => ({ ...s, profile_photo: v }))}
            testId="venue-logo-uploader"
            aspectClass="aspect-square max-w-[220px]"
          />
          <PhotoUploader
            label="Cover photo"
            value={form.cover_photo}
            onChange={(v) => setForm((s) => ({ ...s, cover_photo: v }))}
            testId="venue-cover-uploader"
            aspectClass="aspect-[16/7]"
          />
        </div>
        <GalleryUploader
          value={form.gallery}
          onChange={(v) => setForm((s) => ({ ...s, gallery: v }))}
        />
      </section>

      {err && (
        <div
          role="alert"
          data-testid="venue-form-error-text"
          className="text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md px-3 py-2"
        >
          {err}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            data-testid="venue-form-cancel-button"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={submitting}
          className="bg-[#FF6A00] hover:bg-[#E85F00] text-white"
          data-testid="venue-form-submit-button"
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function buildInitial(initial, defaultCountry) {
  const v = initial || {};
  return {
    business_name: v.business_name || "",
    business_type: v.business_type || "cafe",
    description: v.description || "",
    address: v.address || "",
    city: v.city || "",
    country: v.country || defaultCountry || "",
    postal_code: v.postal_code || "",
    phone: v.phone || "",
    business_email: v.business_email || "",
    website: v.website || "",
    latitude: v.latitude ?? "",
    longitude: v.longitude ?? "",
    accepts_buskers: v.accepts_buskers !== undefined ? !!v.accepts_buskers : true,
    profile_photo: v.profile_photo || null,
    cover_photo: v.cover_photo || null,
    gallery: Array.isArray(v.gallery) ? v.gallery : [],
    hours: normalizeHoursObject(v.hours),
  };
}
